import React from 'react';
import { FiX, FiZap } from 'react-icons/fi';
import './WhatsNewModal.css';

// ─── Update this constant + RELEASES on every new version ─────────────────────
export const APP_VERSION = '0.9.4';

// Entries are shown newest-first down to (but not including) lastSeenVersion.
// Each release: { date, sections: { Added|Changed|Fixed|Removed: [string] } }
const RELEASES = [
  {
    version: '0.9.4',
    date: 'February 27, 2026',
    sections: {
      Added: [
        'AI Literature Review — click "Lit Review" on any collection to generate a structured, citation-formatted literature review. Choose a focus angle and target length (Short / Medium / Long). Copy or download as Markdown.',
      ],
    },
  },
  {
    version: '0.9.3',
    date: 'February 27, 2026',
    sections: {
      Added: [
        'Assignment notifications — when a task is assigned to you, you now receive an email with the task title, priority, due date, and collection',
        'Overdue reminders — a daily check sends a one-time email when a task you own or are assigned to is past its due date',
      ],
    },
  },
  {
    version: '0.9.2',
    date: 'February 27, 2026',
    sections: {
      Added: [
        'Homepage dashboard — default landing page after login with stat chips, recent collections, active monitors, open action items, and your teams',
        'Home button in the header — click the logo or the Home nav button to return to the dashboard from anywhere',
      ],
    },
  },
  {
    version: '0.9.1',
    date: 'February 27, 2026',
    sections: {
      Fixed: [
        '"Back to Quick Start" button is now clearly visible against the header background',
      ],
    },
  },
  {
    version: '0.9.0',
    date: 'February 27, 2026',
    sections: {
      Added: [
        'Quick Start checklist — role-aware onboarding guide (PI / IC) with progress tracking and per-step Go buttons',
        'Role Selection modal on first login — choose Principal Investigator or Individual Contributor',
        'Support Tickets — contact us from the Help button in the header; you get a confirmation email',
        'Download button for context documents in Collections',
        'Quick Start and Help buttons added to the header for all logged-in users',
      ],
    },
  },
  {
    version: '0.8.0',
    date: 'February 26, 2026',
    sections: {
      Added: [
        'Search field selector — target Title & Abstract, Title only, Abstract only, or Full Text',
        'Search mode selector — switch between Boolean and Simple (keyword) search',
      ],
      Fixed: [
        'Facets endpoint now returns the full journal/institution list',
        'Author filter rebuilt on ID-based autocomplete for accuracy',
      ],
    },
  },
  {
    version: '0.7.0',
    date: 'February 20, 2026',
    sections: {
      Added: [
        'Faceted search — filter by journal and institution (include or exclude)',
        'OpenAlex topic filter for improved search precision',
        'Weekly team digest — email summary of team activity sent to PI/owners',
      ],
    },
  },
  {
    version: '0.6.0',
    date: 'February 10, 2026',
    sections: {
      Added: [
        'Admin panel — manage subscriptions, admin access, and enterprise accounts',
        'Password change for authenticated users',
        'Automatic keyword extraction to improve OpenAlex search quality',
      ],
    },
  },
  {
    version: '0.5.0',
    date: 'February 1, 2026',
    sections: {
      Added: [
        'Research Monitoring — create named monitors and get email alerts for new matching papers',
        '"Save This Search" shortcut pre-fills the monitor form',
      ],
    },
  },
  {
    version: '0.4.0',
    date: 'January 20, 2026',
    sections: {
      Added: [
        'Free and Pro individual subscription plans with usage limits',
        'Enterprise accounts with seat-based pooling and owner management',
        'Stripe integration — checkout, webhooks, and yearly billing',
      ],
    },
  },
  {
    version: '0.3.0',
    date: 'January 10, 2026',
    sections: {
      Added: [
        'AI chat with collections — persistent sessions powered by GPT',
        'Context documents — upload PDF/TXT/MD files to ground the chatbot in custom context',
      ],
    },
  },
  {
    version: '0.2.0',
    date: 'December 15, 2025',
    sections: {
      Added: [
        'Team collaboration — create teams, invite members, role-based access',
        'Shared collections at the team level',
        'Notes and action items with assignees and due dates',
      ],
    },
  },
];

const SECTION_ICONS = { Added: '✨', Changed: '🔄', Fixed: '🐛', Removed: '🗑️' };

// Parse a semver string into a comparable number array.
function parseSemver(v = '') {
  return v.split('.').map(Number);
}

// Return true if a > b (semver comparison).
function semverGt(a, b) {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return false;
}

const WhatsNewModal = ({ isOpen, onClose, lastSeenVersion }) => {
  if (!isOpen) return null;

  // Show releases newer than lastSeenVersion (or all if never seen anything).
  const visible = RELEASES.filter(
    (r) => !lastSeenVersion || semverGt(r.version, lastSeenVersion)
  );

  if (visible.length === 0) return null;

  return (
    <div className="wn-overlay" onClick={onClose}>
      <div className="wn-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wn-header">
          <div className="wn-title">
            <FiZap className="wn-title-icon" />
            <h2>What's New</h2>
          </div>
          <button className="wn-close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="wn-body">
          {visible.map((release) => (
            <div key={release.version} className="wn-release">
              <div className="wn-release-header">
                <span className="wn-version">v{release.version}</span>
                <span className="wn-date">{release.date}</span>
              </div>
              {Object.entries(release.sections).map(([section, items]) => (
                <div key={section} className="wn-section">
                  <h4 className="wn-section-title">
                    {SECTION_ICONS[section] || '•'} {section}
                  </h4>
                  <ul className="wn-items">
                    {items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="wn-footer">
          <button className="wn-got-it" onClick={onClose}>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
